import { Button, cn, DropdownMenu, IconButton, Input, Popover, Tooltip } from "@z/ui";
import {
  Bold,
  Italic,
  Link,
  Plus,
  Pilcrow,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  ListTodo,
  Quote,
  CodeXml,
  Undo2,
  Redo2,
  Ellipsis,
} from "lucide-react";
import { useRef, useState } from "react";
import { safeLink, type BlockKind } from "./markdown";

/**
 * The link being edited. `selected` when there is text (or a link) to put it on; otherwise the
 * link is inserted with the text given here.
 */
export type LinkDraft = { url: string; title: string; selected: boolean };

export const blocks: { id: BlockKind; title: string; icon: typeof Pilcrow }[] = [
  { id: "paragraph", title: "Paragraph", icon: Pilcrow },
  { id: "heading1", title: "Heading 1", icon: Heading1 },
  { id: "heading2", title: "Heading 2", icon: Heading2 },
  { id: "heading3", title: "Heading 3", icon: Heading3 },
  { id: "list", title: "Bullet list", icon: List },
  { id: "ordered", title: "Numbered list", icon: ListOrdered },
  { id: "task", title: "Checklist", icon: ListTodo },
  { id: "quote", title: "Quote", icon: Quote },
  { id: "code", title: "Code block", icon: CodeXml },
];
function LinkForm({
  link,
  onSave,
}: {
  link: LinkDraft;
  onSave: (title: string, url: string | null) => void;
}) {
  const [title, setTitle] = useState(link.title),
    [url, setUrl] = useState(link.url);
  const valid = safeLink(url);
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid) onSave(title || valid, valid);
      }}
    >
      <Popover.Title>Edit link</Popover.Title>
      {!link.selected && (
        <label className="flex flex-col gap-1 text-xs text-fg-muted">
          Text
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
      )}
      <label className="flex flex-col gap-1 text-xs text-fg-muted">
        URL
        <Input
          type="text"
          placeholder="https://example.com"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </label>
      {url && !valid && (
        <p className="text-xs text-danger">
          Use a web address, mailto: address, or an app-relative path.
        </p>
      )}
      <div className="flex items-center justify-between gap-2">
        <Button size="sm" variant="ghost" onClick={() => onSave(title, null)}>
          Remove link
        </Button>
        <Button size="sm" type="submit" disabled={!valid}>
          Save link
        </Button>
      </div>
      {safeLink(link.url) && (
        <a
          className="text-xs text-accent"
          href={safeLink(link.url)!}
          target={link.url.startsWith("/") ? "_self" : "_blank"}
          rel="noopener noreferrer"
        >
          Open link ↗
        </a>
      )}
    </form>
  );
}

export function EditorTools({
  expanded = false,
  active,
  canUndo,
  canRedo,
  focus,
  onFormat,
  link,
  onCloseLink,
  onSaveLink,
  onAddTodo,
  canAddTodo = false,
}: {
  expanded?: boolean;
  active: ReadonlySet<string>;
  canUndo: boolean;
  canRedo: boolean;
  focus: () => HTMLElement | null;
  onFormat: (kind: string) => void;
  link: LinkDraft | null;
  onCloseLink: () => void;
  onSaveLink: (title: string, url: string | null) => void;
  onAddTodo?: (() => void) | undefined;
  canAddTodo?: boolean;
}) {
  const restoreEditorFocus = useRef(true);
  return (
    <div
      role="toolbar"
      aria-label="Document formatting"
      className={cn(
        "flex min-h-8 items-center gap-1 text-fg-muted",
        expanded ? "min-w-0 flex-1" : "ml-auto shrink-0 justify-end",
      )}
    >
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          render={
            <Button
              aria-label="Insert formatting"
              variant="ghost"
              size="sm"
              className={cn("gap-1.5 font-normal", !expanded && "size-10 p-0 text-fg-muted")}
            >
              <Plus className={expanded ? "text-accent" : undefined} />
              {expanded && <span className="@max-[400px]/editor:hidden">Insert</span>}
            </Button>
          }
        />
        <DropdownMenu.Content aria-label="Formatting" finalFocus={focus}>
          {blocks.map(({ id, title, icon: Icon }) => (
            <DropdownMenu.Item key={id} onClick={() => onFormat(id)}>
              <Icon />
              {title}
            </DropdownMenu.Item>
          ))}
          <DropdownMenu.Separator />
          <DropdownMenu.Item onClick={() => onFormat("link")}>
            <Link />
            Link
          </DropdownMenu.Item>
          <DropdownMenu.Item disabled={!canUndo} onClick={() => onFormat("undo")}>
            <Undo2 />
            Undo
          </DropdownMenu.Item>
          <DropdownMenu.Item disabled={!canRedo} onClick={() => onFormat("redo")}>
            <Redo2 />
            Redo
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
      {expanded && (
        <>
          <span aria-hidden="true" className="mx-1 h-4 w-px bg-border @max-[640px]/editor:hidden" />
          {[
            { id: "undo", title: "Undo", icon: Undo2 },
            { id: "redo", title: "Redo", icon: Redo2 },
            { id: "heading2", title: "Heading", icon: Heading2 },
            { id: "**", title: "Bold", icon: Bold },
            { id: "*", title: "Italic", icon: Italic },
            { id: "list", title: "Bullet list", icon: List },
            { id: "task", title: "Checklist", icon: ListTodo },
            { id: "link", title: "Link", icon: Link },
          ].map(({ id, title, icon: Icon }) => (
            <Tooltip key={id} content={title}>
              <IconButton
                label={title}
                disabled={id === "undo" ? !canUndo : id === "redo" ? !canRedo : false}
                aria-pressed={id === "undo" || id === "redo" ? undefined : active.has(id)}
                className={cn(
                  "aria-pressed:bg-accent-muted aria-pressed:text-accent",
                  "@max-[640px]/editor:hidden",
                )}
                size="sm"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onFormat(id)}
              >
                <Icon />
              </IconButton>
            </Tooltip>
          ))}
          <span className="flex-1" />
        </>
      )}
      {!expanded && onAddTodo && (
        <DropdownMenu.Root
          onOpenChange={(open) => {
            if (open) restoreEditorFocus.current = true;
          }}
        >
          <DropdownMenu.Trigger
            render={
              <IconButton label="Note options" className="size-10 text-fg-muted">
                <Ellipsis />
              </IconButton>
            }
          />
          <DropdownMenu.Content
            aria-label="Note options"
            align="end"
            finalFocus={() => (restoreEditorFocus.current ? focus() : false)}
          >
            <DropdownMenu.Item
              disabled={!canAddTodo}
              onClick={() => {
                // The task composer owns focus after this menu closes.
                restoreEditorFocus.current = false;
                onAddTodo();
              }}
            >
              <ListTodo />
              Add as task
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      )}
      <Popover.Root
        open={link !== null}
        onOpenChange={(open) => {
          if (!open) onCloseLink();
        }}
      >
        <Popover.Trigger
          render={
            <IconButton label="Link details" size="sm" className={link ? "" : "hidden"}>
              <Link />
            </IconButton>
          }
        />
        <Popover.Content align="end" finalFocus={false}>
          {link && (
            <LinkForm key={`${link.selected}:${link.url}`} link={link} onSave={onSaveLink} />
          )}
        </Popover.Content>
      </Popover.Root>
    </div>
  );
}
